import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { AccountTransaction } from '@shared/contract';

import { brand } from '../theme';
import StatusChip, { ActivityGlyph, type StatusTone } from '../ui/StatusChip';
import { relativeTime } from '../ui/relativeTime';

function typeLabel(tx: AccountTransaction): string {
  if (tx.type === 'CREDIT') return 'Deposit';
  if (tx.type === 'DEBIT') return 'Withdraw';
  if (tx.type === 'REVERSAL') return 'Reversal';
  return String(tx.type);
}

function glyph(tx: AccountTransaction): string {
  if (tx.isFlagged) return '⚑';
  if (tx.type === 'CREDIT') return '↓';
  if (tx.type === 'REVERSAL') return '↺';
  return '↑';
}

/** "PENDING" -> "Pending" for statuses we do not have bespoke copy for. */
function sentenceCase(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function statusLabel(tx: AccountTransaction): string {
  if (tx.isFlagged) return 'Flagged for review';
  if (tx.type === 'DEBIT' && tx.status === 'PENDING') {
    // Spans merchant hold through on-chain broadcast — Cheddar sends nothing at approval time.
    return 'Awaiting merchant approval';
  }
  if (tx.type === 'DEBIT' && tx.status === 'FAILED') {
    return tx.statusReason ? `Failed · ${tx.statusReason}` : 'Failed';
  }
  return sentenceCase(String(tx.status));
}

function statusTone(tx: AccountTransaction): StatusTone {
  if (tx.isFlagged) return 'pending';
  if (tx.status === 'FAILED') return 'failed';
  if (tx.status === 'PENDING') return 'pending';
  if (tx.status === 'CONFIRMED') return 'success';
  return 'neutral';
}

/** Explanatory note shown under a row when the status needs a sentence, not a chip. */
function note(tx: AccountTransaction): string | null {
  if (tx.isFlagged) {
    return 'This deposit is held pending a routine compliance check. Funds stay in your balance but are not yet available to withdraw.';
  }
  if (tx.type === 'DEBIT' && tx.status === 'PENDING') {
    return `Awaiting merchant approval. This can stay pending until the send confirms on-chain.${
      tx.statusReason ? ` (${tx.statusReason})` : ''
    }`;
  }
  if (tx.type === 'DEBIT' && tx.status === 'FAILED') {
    return `Withdrawal failed${
      tx.statusReason ? `: ${tx.statusReason}` : ''
    }. This withdrawal was reversed.`;
  }
  return null;
}

/**
 * Single history row. Withdraw pending/failed copy lives here — do not fork the list.
 */
export default function TransactionRow({ tx }: { tx: AccountTransaction }) {
  const networkLabel =
    tx.isTestnet === undefined ? 'Network unknown' : tx.isTestnet ? 'Testnet' : 'Mainnet';
  const detail = note(tx);
  const tone = statusTone(tx);

  // Amounts on the ledger are positive; render a single debit sign for DEBIT/REVERSAL.
  const signed = tx.type === 'DEBIT' || tx.type === 'REVERSAL' ? '−' : '+';

  return (
    <Box sx={{ py: 1.75, borderBottom: `1px solid ${brand.hairlineSoft}`, '&:last-of-type': { borderBottom: 0 } }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <ActivityGlyph tone={tone} symbol={glyph(tx)} />

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.005em' }}>
            {typeLabel(tx)} {signed}
            {tx.amount}
            {tx.symbol ? ` ${tx.symbol}` : ''}
          </Typography>

          <StatusChip tone={tone}>{statusLabel(tx)}</StatusChip>

          <Typography sx={{ fontSize: 12, color: brand.faint }}>
            {[tx.chain, networkLabel].filter(Boolean).join(' · ')}
            {tx.usdAmount ? ` · ≈ $${tx.usdAmount} USD` : ''}
          </Typography>

          {detail ? (
            <Typography
              sx={{
                mt: 0.5,
                px: 1.5,
                py: 1,
                borderRadius: '12px',
                backgroundColor: brand.canvasSunk,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: brand.muted,
                textWrap: 'pretty',
              }}
            >
              {detail}
            </Typography>
          ) : null}

          {tx.txHash && !tx.explorerUrl ? (
            <Typography
              sx={{ fontFamily: brand.mono, fontSize: 11.5, color: brand.faint, wordBreak: 'break-all' }}
            >
              {tx.txHash}
            </Typography>
          ) : null}
        </Stack>

        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: 12, color: brand.faint, whiteSpace: 'nowrap' }}
            title={new Date(tx.createdAt).toLocaleString()}
          >
            {relativeTime(tx.createdAt)}
          </Typography>
          {tx.explorerUrl ? (
            <Link
              href={tx.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12.5 }}
            >
              Explorer ↗
            </Link>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}
